import Phaser from 'phaser';
import i18next from 'i18next';
import { GAME_WIDTH, GAME_HEIGHT } from '@/game/config';
import { ensureScriptFont } from '@/game/fonts';
import { trackReady } from '@/analytics';

export class PreloadScene extends Phaser.Scene {
  constructor() {
    super({ key: 'Preload' });
  }

  preload(): void {
    const bar = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, 400, 8, 0x333333);
    const fill = this.add.rectangle(GAME_WIDTH / 2 - 200, GAME_HEIGHT / 2, 0, 8, 0xffffff).setOrigin(0, 0.5);
    this.load.on('progress', (p: number) => fill.width = 400 * p);
    this.load.on('complete', () => { bar.destroy(); fill.destroy(); });

    // Missing files are tolerated (BoardScene falls back to a starfield).
    this.load.on('loaderror', (file: Phaser.Loader.File) => {
      console.warn(`[Preload] asset missing: ${file.key} (${file.src})`);
    });
    // Background images are NOT loaded here — BoardScene loads the chosen one on demand, so the
    // menu appears without waiting on backdrops it never shows.

    // Journey art: the end-of-journey reward stars + rocket marker for the progress bar. Each
    // journey pool names its reward texture (see Pack.reward); the reading journey uses the ABC
    // Star, the fables journey the Unicorn Star.
    this.load.image('abc_star', '/images/abc-star.png');
    this.load.image('unicorn_star', '/images/unicorn-star.png');
    this.load.image('adventure_planet', '/images/adventure-planet.png');
    this.load.image('rainbow_eye', '/images/rainbow-eye.png');
    this.load.image('rocket', '/images/rocket.png');
    // Arcade-lesson reward stars (generated SVGs — the browser rasterizes them at load).
    // Torii = JA particles, Taegeuk = KO particles, Elephant = TH classifiers, Tones = TH tones.
    this.load.svg('torii_star', '/images/torii-star.svg', { width: 256, height: 256 });
    this.load.svg('taegeuk_star', '/images/taegeuk-star.svg', { width: 256, height: 256 });
    this.load.svg('elephant_star', '/images/elephant-star.svg', { width: 256, height: 256 });
    this.load.svg('tones_star', '/images/tones-star.svg', { width: 256, height: 256 });
    this.load.svg('puzzle_star', '/images/puzzle-star.svg', { width: 256, height: 256 });
    // Der-Die-Das star — the German article journey (de + de-CH only).
    this.load.svg('artikel_star', '/images/artikel-star.svg', { width: 256, height: 256 });
    // Bienen-Stern — "i oder ie?", the German spelling lesson (de + de-CH only).
    this.load.svg('ie_star', '/images/ie-star.svg', { width: 256, height: 256 });
    // Zwillings-Stern — "Eins oder zwei?", the doubled-consonant lesson (de + de-CH only).
    this.load.svg('doppelt_star', '/images/doppelt-star.svg', { width: 256, height: 256 });
    // Gummiband-Stern — "d oder t?", the German final-consonant lesson (de + de-CH only).
    this.load.svg('auslaut_star', '/images/auslaut-star.svg', { width: 256, height: 256 });
    // 받침별 — "이? 가?", the Korean particle lesson (ko only).
    this.load.svg('josa_star', '/images/josa-star.svg', { width: 256, height: 256 });
    // 물음표별 — "누구? 어디?", the Korean question-word lesson (ko only).
    this.load.svg('uimunsa_star', '/images/uimunsa-star.svg', { width: 256, height: 256 });
    // Whisper Star — "sh, ch or th?", the English digraph lesson (en only).
    this.load.svg('digraphs_star', '/images/digraphs-star.svg', { width: 256, height: 256 });
    // Wand Star — "Magic e?", the English silent-e lesson (en only).
    this.load.svg('magic_e_star', '/images/magic-e-star.svg', { width: 256, height: 256 });
    // Étoile Chut — "Quelle lettre muette ?", the French silent-letter lesson (fr only).
    this.load.svg('muette_star', '/images/muette-star.svg', { width: 256, height: 256 });
    // Étoile Accent — "a ou à ?", the French homophone lesson (fr only).
    this.load.svg('accent_star', '/images/accent-star.svg', { width: 256, height: 256 });
    // Estrella Erre — "¿r o rr?", the Spanish r/rr lesson (es only).
    this.load.svg('erre_star', '/images/erre-star.svg', { width: 256, height: 256 });
    // Estrella Tilde — "¿Aguda, llana o esdrújula?", the Spanish stress lesson (es only).
    this.load.svg('tilde_star', '/images/tilde-star.svg', { width: 256, height: 256 });
    // Rekster — "d of t?", the Dutch verlengwoord lesson (nl only).
    this.load.svg('verleng_star', '/images/verleng-star.svg', { width: 256, height: 256 });
    // dt-ster — "d, t of dt?", the Dutch verb-spelling lesson (nl only).
    this.load.svg('werkwoord_star', '/images/werkwoord-star.svg', { width: 256, height: 256 });
    // Pumpkin — "Spooky Words", the Halloween event lesson's journey reward (en + de, 21 Oct–10 Nov).
    this.load.svg('pumpkin_star', '/images/pumpkin-star.svg', { width: 256, height: 256 });
    // Satzbähnli — "Bau den Satz", the word-order lesson (de-CH only for now).
    this.load.svg('satz_bahn', '/images/satz-bahn.svg', { width: 256, height: 256 });
    // Lese-Lupe — "Lies und antworte", the comprehension lesson (de-CH only for now).
    this.load.svg('lese_lupe', '/images/lese-lupe.svg', { width: 256, height: 256 });
  }

  async create(): Promise<void> {
    // Make sure the non-Latin web font is ready before any scene renders Thai/JP/KR text,
    // otherwise canvas falls back to system fonts that may drop combining marks (e.g. Thai tones).
    await ensureScriptFont(i18next.language);
    // Web demo only (a no-op everywhere else). Here rather than in MenuScene.create(), which also
    // runs after every round and every lesson switch — this line runs exactly once per page load,
    // which is what makes it comparable with the page view.
    trackReady();
    this.scene.start('Menu');
  }
}
